/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import {ErrorMessage, Field, Formik, getIn} from "formik";
import ContainerFluid from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import UCButton from "../../buttons";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {usePeopleService} from "../../../services/peopleService";

/* Components */
import MultiValueField from "../../../components/ucMultiValueField/index";

/* Styles */
import styles from "./styles.js"


const messages = defineMessages({
  citiPcanPlaceholderMessage: {
    id: 'editCitiPCANsModal.primaryPCANsPlaceholder'
  },
  aupcanPlaceholderMessage: {
    id: 'editCitiPCANsModal.citiAuPCANsPlaceholder'
  },
})

const Content = props => {
  const { setShowModal, primaryPCAN, auPCAN, userId, setPrimaryField, setAuPCANField, entityPCANLimit} = props;

  const theme = useTheme();
  const classes = styles({ theme });

  const { checkPermission } = useHasPermission();

  const formRef = useRef(null);
  const intl = useIntl();

  const { editCitiPCANMockedApiValidationObj } = useValidationService()
  const [testSchema, setTestSchema] = useState({});
  const { editUserPCANs } = usePeopleService()

  const [errorMessage, setErrorMessage] = useState(false)
  const [exitingAuPCAN, setExitingAuPCAN] = useState(false)
  const [exitingPrimaryPCAN, setExitingPrimaryPCAN] = useState(false)

  useEffect(() => {
    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(editCitiPCANMockedApiValidationObj);
    setTestSchema(result)
  }, [])

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values, setSubmitting) => {
    const response = await editUserPCANs(values, userId)
    if (response.status === "ERROR") {
      const errorCode = response?.errors[0]?.errorCode;
      if (errorCode === '1997') {
        setExitingAuPCAN(true)
      } else if (errorCode === '1996') {
        setExitingPrimaryPCAN(true)
      }
    }else if (response.status === "SUCCESS") {
      setShowModal(false)
      setSubmitting(false);
      setPrimaryField(values.primaryPCAN)
      setAuPCANField(values.auPCAN)
    }
  }

  const checkValues = (values) => {
    if (values.primaryPCAN?.length > 0 && values.auPCAN?.length > 0){
      setErrorMessage(true)
    }else {setErrorMessage(false)}
  }

  const primaryChange = (item, setFieldValue) => {
    setExitingPrimaryPCAN(false)
    setFieldValue("primaryPCAN", item);
  };
  const auPCANChange = (item, setFieldValue) => {
    setExitingAuPCAN(false)
    setFieldValue("auPCAN", item);
  };

  const maxPCANsAllowed = parseInt(entityPCANLimit) || 2

  return (
    <div className={classes.modalContent}>
      <Formik
        innerRef={formRef}
        initialValues={{
          primaryPCAN: primaryPCAN || [],
          auPCAN: auPCAN || []
        }}
        validationSchema={testSchema}
        onSubmit={(values, { setSubmitting }) => {
          submitForm(values, setSubmitting);
        }}
      >
        {({
            values,
            errors,
            touched,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) => (

          <>
            <form onSubmit={handleSubmit} onChange={checkValues(values)}>
              <div className={classes.inputContainer}>
                <label htmlFor="primaryPCAN" className={classes.label}>
                  <FormattedMessage id="editCitiPCANsModal.primaryPCAN"/>
                </label>
                <MultiValueField
                  name="primaryPCAN"
                  values={values.primaryPCAN}
                  onValuesChange={item => primaryChange(item, setFieldValue)}
                  setFieldTouched={setFieldTouched}
                  placeholder={intl.formatMessage(messages.citiPcanPlaceholderMessage)}
                  maxValues={maxPCANsAllowed}
                  errors={errors}
                  ariaDescribedby={"primaryPCAN-error"}
                />
                {
                  errorMessage ?
                    <div className={classes.error}>
                      <FormattedMessage id="editCitiPCANsModal.errorMessageText"/>
                    </div>
                    :
                    <ErrorMessage
                      name="primaryPCAN"
                      component="span"
                      className={classes.error}
                      id="primaryPCAN-error"
                      role="alert"
                      aria-live="assertive"
                    />
                }
                {exitingPrimaryPCAN && (
                  <div className={classes.nameError}>
                    <FormattedMessage id="editCitiPCANsModal.primaryErrorMessageText" />
                  </div>
                )}
              </div>
              <div className={classes.inputContainer}>
                <label htmlFor="auPCAN" className={classes.label}>
                  <FormattedMessage id="editCitiPCANsModal.citiAuPCAN"/>
                </label>
                <MultiValueField
                  name="auPCAN"
                  values={values.auPCAN}
                  onValuesChange={item => auPCANChange(item, setFieldValue)}
                  setFieldTouched={setFieldTouched}
                  placeholder={intl.formatMessage(messages.aupcanPlaceholderMessage)}
                  maxValues={maxPCANsAllowed}
                  errors={errors}
                  ariaDescribedby={"auPCAN-error"}
                />
                {
                  errorMessage ?
                    <div className={classes.error}>
                      <FormattedMessage id="editCitiPCANsModal.errorMessageText"/>
                    </div>
                    :
                    <ErrorMessage
                      name="auPCAN"
                      component="span"
                      className={classes.error}
                      id="auPCAN-error"
                      role="alert"
                      aria-live="assertive"
                    />

                }
                {exitingAuPCAN && (
                  <div className={classes.nameError}>
                    <FormattedMessage id="editCitiPCANsModal.auPCANErrorMessageText" />
                  </div>
                )}
              </div>
              <div className={classes.btnContainer}>
                <ContainerFluid>
                  <Row>
                    <Col className={classes.colCancel}>
                      <UCButton
                        onClick={() => setShowModal(false)}
                        type="default"
                        className={classes.cancelBtn}
                        text={<FormattedMessage id="editCitiPCANsModal.cancel"/>}
                      />
                    </Col>
                    <Col className={classes.colRemove}>
                      <UCButton
                        className={classes.createBtn}
                        onClick={handleFormSubmit}
                        type="primary"
                        text={<FormattedMessage id="editCitiPCANsModal.save"/>}
                        spinner={{
                          name: "editUserPCANSpinner",
                          size: 20,
                        }}
                      />
                    </Col>
                  </Row>
                </ContainerFluid>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  )
}

export default Content;